import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import {
  GetCreditBook,
  ICreditBookListItem,
  PostCreditBook,
  PostAdjustment,
} from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetTag } from "api/rpc/2024-04/facilityAdmin/client/tags";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";

import Page from "components/page/Page";
import Search from "components/search/Search";
import { ButtonNew as Button } from "components/buttonNew";
import CreditBookCustomerTags from "./CreditBookCustomerTags";
import Sheet from "components/sheet/Sheet";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import CustomerCreditAdjustModal from "../tabs/credit/CustomerCreditAdjustModal";
import CreditBooksFilterDrawer, { ICreditBooksFilterValues } from "./CreditBooksFilterDrawer";
import CreditBooksAppliedFilters from "./CreditBooksAppliedFilters";
import CreditBookSelectCustomer from "./CreditBookSelectCustomer";
import { emptyFilters } from "./creditBooksFilterUtils";

import DataTable from "../../../../../components/dataTable/DataTable";

import { ICustomer } from "redux/reducers/models/customer";

import "./creditBooks.scss";

const TABLE_LIMIT = 10;

const tableColumns = (t: TFunction<"translation", undefined>) => {
  return [
    { label: t("Title"), width: "auto" },
    { label: t("Customer"), width: "auto" },
    { label: t("Current balance"), width: "auto" },
    { label: t("Last transaction"), id: "last_transaction" as const, width: "auto" },
    { label: t("Customer Type"), width: "auto" },
    { label: t("Customer tag"), width: "auto" },
    { label: "", width: "48px" },
  ];
};

interface IFilters extends ICreditBooksFilterValues {
  search: string;
  offset: number;
  limit: number;
  sort_by: "last_transaction" | "balance" | "title";
  sort_direction: "asc" | "desc";
}

export default function CreditBooks() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { displayCurrency } = useCurrencyFacility();
  const { authStore, facilityStore } = useAppSelector(store => store);
  const permissions = authStore.user?.permissions;

  const [creditBooks, setCreditBooks] = useState<ICreditBookListItem[]>(undefined);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [customerTags, setCustomerTags] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [showNewCreditBookModal, setShowNewCreditBookModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedCreditBook, setSelectedCreditBook] = useState<ICreditBookListItem>(null);
  const [newCreditBook, setNewCreditBook] = useState({ title: "", customer: null as ICustomer | null });
  const [customerIdsWithCreditBook, setCustomerIdsWithCreditBook] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<IFilters>({
    search: "",
    offset: 0,
    limit: TABLE_LIMIT,
    ...emptyFilters(),
    sort_by: "last_transaction",
    sort_direction: "desc",
  });

  useEffect(() => {
    void loadCustomerTypes();
    void loadCustomerTags();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCreditBooks(source.token);
    return () => {
      source.cancel();
    };
  }, [filters]);

  async function loadCustomerTypes() {
    const res = await GetCustomerType({ limit: 1000 }, false);
    if (res.status === StatusCode.OK) {
      setCustomerTypes(res.data);
    }
  }

  async function loadCustomerTags() {
    const res = await GetTag({ type: "customer" }, false);
    if (res.status === StatusCode.OK) {
      setCustomerTags(res.data);
    }
  }

  async function loadCustomersWithCreditBooks() {
    const res = await GetCreditBook({ type: "credit_book", has_customer: true }, false);
    if (res.status !== StatusCode.OK || !Array.isArray(res.data)) {
      setCustomerIdsWithCreditBook(new Set());
      return;
    }

    const ids = new Set(
      (res.data as ICreditBookListItem[])
        .map(creditBook => creditBook.customer_id)
        .filter((customerId): customerId is number => Boolean(customerId)),
    );
    setCustomerIdsWithCreditBook(ids);
  }

  function openNewCreditBookModal(): void {
    setShowNewCreditBookModal(true);
    void loadCustomersWithCreditBooks();
  }

  function buildRequestParams(includePagination = true): Parameters<typeof GetCreditBook>[0] {
    const params: Parameters<typeof GetCreditBook>[0] = {
      type: "credit_book",
      has_customer: true,
      with_customer: true,
      sort_by: filters.sort_by,
      sort_direction: filters.sort_direction,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (includePagination) {
      params.limit = TABLE_LIMIT;
      params.offset = filters.offset;
    }

    if (filters.customer_type_ids.length > 0) {
      params.customer_type_ids = filters.customer_type_ids;
    }

    if (filters.tag_ids.length > 0) {
      params.tag_ids = filters.tag_ids;
    }

    if (filters.balance_min !== "") {
      params.balance_min = Number(filters.balance_min);
    }

    if (filters.balance_max !== "") {
      params.balance_max = Number(filters.balance_max);
    }

    return params;
  }

  async function loadCreditBooks(token?: CancelToken) {
    if (creditBooks !== undefined) {
      setCreditBooks(undefined);
    }

    const res = await GetCreditBook(buildRequestParams(), false, token);

    if (token?.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting credit books")));
      setCreditBooks([]);
      return;
    }

    setCreditBooks(Array.isArray(res.data) ? (res.data as ICreditBookListItem[]) : []);
  }

  async function exportCreditBooks() {
    const res = await GetCreditBook(buildRequestParams(false), true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting credit books")));
      return;
    }

    const books: ICreditBookListItem[] = Array.isArray(res.data) ? (res.data as ICreditBookListItem[]) : [];

    const headers = [
      t("Customer"),
      t("Email"),
      t("Current balance"),
      t("Last transaction"),
      t("Customer Type"),
      t("Customer tag"),
    ];

    const rows: string[][] = books.map(creditBook => getCreditBookExportRow(creditBook));

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n");
    const exportLink = document.createElement("a");
    exportLink.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
    exportLink.target = "_blank";
    exportLink.download = `credit_books_${moment().format("YYYY-MM-DD")}.csv`;
    exportLink.click();
  }

  async function createCreditBook() {
    if (!newCreditBook.customer) {
      dispatch(showError(t("Please select a customer")));
      return;
    }

    if (!newCreditBook.title.trim()) {
      dispatch(showError(t("Title must not be empty")));
      return;
    }

    const res = await PostCreditBook(
      {
        title: newCreditBook.title.trim(),
        customer_id: newCreditBook.customer.id,
        type: "credit_book",
        currency: facilityStore?.facility?.default_currency_code ?? "cad",
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating new Credit Book")));
      return;
    }

    dispatch(showSuccess(t("Credit book created successfully")));
    setShowNewCreditBookModal(false);
    setNewCreditBook({ title: "", customer: null });
    void loadCreditBooks();
  }

  async function adjustCreditBook(amount: string, note: string) {
    if (!selectedCreditBook) {
      return;
    }

    const res = await PostAdjustment({ credit_book_id: selectedCreditBook.id, amount, note }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adjusting credit book balance")));
      return;
    }

    setShowAdjustModal(false);
    setSelectedCreditBook(null);
    void loadCreditBooks();
  }

  function applyDrawerFilters(drawerFilters: ICreditBooksFilterValues): void {
    setFilters(prev => ({
      ...prev,
      ...drawerFilters,
      offset: 0,
    }));
  }

  function removeAppliedFilter(filterKey: "balance" | "customer_type" | "customer_tag"): void {
    setFilters(prev => {
      switch (filterKey) {
        case "balance":
          return { ...prev, balance_min: "", balance_max: "", offset: 0 };
        case "customer_type":
          return { ...prev, customer_type_ids: [], offset: 0 };
        case "customer_tag":
          return { ...prev, tag_ids: [], offset: 0 };
        default:
          return prev;
      }
    });
  }

  function clearAllAppliedFilters(): void {
    setFilters(prev => ({
      ...prev,
      ...emptyFilters(),
      offset: 0,
    }));
  }

  function formatLastTransactionText(date: unknown): string {
    if (typeof date !== "string" || !date) {
      return "";
    }

    return moment(date).format("MMM DD, YYYY");
  }

  function formatLastTransaction(date: unknown) {
    if (typeof date !== "string" || !date) {
      return <p className="field-unavailable">{t("N/A")}</p>;
    }

    return formatLastTransactionText(date);
  }

  function getCreditBookExportRow(creditBook: ICreditBookListItem): string[] {
    const tagLabel = creditBook.customer?.tags?.[0]?.label;

    return [
      String(creditBook.customer?.full_name ?? ""),
      String(creditBook.customer?.email ?? ""),
      String(creditBook.balance),
      formatLastTransactionText(creditBook.last_transaction_at),
      String(creditBook.customer?.customer_type ?? ""),
      typeof tagLabel === "string" ? tagLabel : "",
    ];
  }

  function formatCustomerTag(creditBook: ICreditBookListItem) {
    return <CreditBookCustomerTags tags={creditBook.customer?.tags ?? []} popoverId={creditBook.id} />;
  }

  const secondaryActions = [
    {
      content: (
        <>
          <FontAwesomeIcon icon={["far", "arrow-up-from-bracket"]} className="mr-2" />
          {t("Export list")}
        </>
      ),
      action: (): void => {
        void exportCreditBooks();
      },
    },
  ];

  const primaryAction = {
    content: (
      <>
        <FontAwesomeIcon icon={["far", "plus"]} className="mr-2" />
        {t("New credit book")}
      </>
    ),
    action: openNewCreditBookModal,
  };

  return (
    <div className="credit-books-page">
      <Page
        title={t("Credit Books")}
        subtitle={t("View and manage the credit book for all customers of your facility here.")}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
      >
        <div className="credit-books-search-row">
          <div className="credit-books-search">
            <Search
              historyKey="facility-customer-credit-books"
              searchCallback={(searchValue: string) =>
                searchValue !== filters.search
                  ? setFilters(prev => ({ ...prev, search: searchValue, offset: 0 }))
                  : null
              }
              placeholder={t("Search by customer name or email...")}
            />
          </div>
          <Button type="secondary" size="medium" onClick={() => setFilterDrawerOpen(true)}>
            <FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
            {t("Filter by")}
          </Button>
        </div>

        <CreditBooksAppliedFilters
          filters={{
            balance_min: filters.balance_min,
            balance_max: filters.balance_max,
            customer_type_ids: filters.customer_type_ids,
            tag_ids: filters.tag_ids,
          }}
          customerTypes={customerTypes}
          customerTags={customerTags}
          onRemoveFilter={removeAppliedFilter}
          onViewMore={() => setFilterDrawerOpen(true)}
          onClearAll={clearAllAppliedFilters}
        />

        <div className="data-table-scroll">
          <DataTable
            columns={tableColumns(t)}
            sort={(key, direction) => {
              if (key === "last_transaction") {
                setFilters(prev => ({
                  ...prev,
                  sort_by: "last_transaction",
                  sort_direction: direction,
                  offset: 0,
                }));
              }
            }}
            footer={{
              tableLimit: filters.limit,
              tableOffset: filters.offset,
              disableNextOffset: !creditBooks || creditBooks.length < filters.limit,
              handleTableOffset: direction =>
                setFilters(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                })),
            }}
            loading={creditBooks === undefined}
          >
            {creditBooks?.map(creditBook => (
              <tr key={creditBook.id}>
                <td>{creditBook.title || <p className="field-unavailable">-</p>}</td>
                <td
                  className="clickable"
                  onClick={() => history.push(`/admin/customers/${String(creditBook.customer_id)}/profile`)}
                >
                  <div className="credit-books-customer-name">{creditBook.customer?.full_name}</div>
                  <div className="credit-books-customer-email">{creditBook.customer?.email}</div>
                </td>
                <td>{displayCurrency(creditBook.balance, creditBook.currency)}</td>
                <td>{formatLastTransaction(creditBook.last_transaction_at)}</td>
                <td>{creditBook.customer?.customer_type ?? <p className="field-unavailable">-</p>}</td>
                <td>{formatCustomerTag(creditBook)}</td>
                <td className="credit-books-actions-cell">
                  <NavigationDropdownNew
                    rightAlign
                    hideChevron
                    type="secondary"
                    label={<FontAwesomeIcon icon={faEllipsisV} />}
                    sections={[
                      [
                        {
                          label: t("View details"),
                          icon: "user",
                          onClick: () => history.push(`/admin/customers/${creditBook.customer_id}/credit`),
                        },
                        {
                          label: t("Adjust Balance"),
                          icon: "arrow-up-arrow-down",
                          disabled: !permissions?.credit_books_make_adjustments,
                          onClick: () => {
                            setSelectedCreditBook(creditBook);
                            setShowAdjustModal(true);
                          },
                        },
                      ],
                    ]}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      </Page>

      {showNewCreditBookModal && (
        <div className="credit-books-create-sheet">
          <Sheet
            open={showNewCreditBookModal}
            size="x-small"
            height="flexible"
            overflow
            closable
            title={t("Create credit book")}
            onCancel={() => {
              setShowNewCreditBookModal(false);
              setNewCreditBook({ title: "", customer: null });
            }}
            onOk={(): void => {
              void createCreditBook();
            }}
            cancelText={t("Cancel")}
            okText={t("Save and Apply")}
            okDisabled={!newCreditBook.customer || newCreditBook.title.trim() === ""}
          >
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <CreditBookSelectCustomer
                    key={
                      showNewCreditBookModal ? "credit-book-customer-select-open" : "credit-book-customer-select-closed"
                    }
                    label={t("Customer")}
                    placeholder={t("Search by customer name or email address...")}
                    customerIdsWithCreditBook={customerIdsWithCreditBook}
                    onCustomerChange={customer => setNewCreditBook(prev => ({ ...prev, customer }))}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    value={newCreditBook.title}
                    label={t("Title")}
                    id="title"
                    placeholder={t("Enter title for credit book...")}
                    onChange={event => setNewCreditBook(prev => ({ ...prev, title: event.target.value }))}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Sheet>
        </div>
      )}

      <CustomerCreditAdjustModal
        open={showAdjustModal}
        onClose={() => {
          setShowAdjustModal(false);
          setSelectedCreditBook(null);
        }}
        onOk={adjustCreditBook}
      />

      <CreditBooksFilterDrawer
        open={filterDrawerOpen}
        appliedFilters={{
          balance_min: filters.balance_min,
          balance_max: filters.balance_max,
          customer_type_ids: filters.customer_type_ids,
          tag_ids: filters.tag_ids,
        }}
        customerTypes={customerTypes}
        customerTags={customerTags}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={applyDrawerFilters}
      />
    </div>
  );
}
